import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Stack,
  Dialog,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Image as ImageIcon,
  Add,
  Close
} from '@mui/icons-material';
import axios from 'axios';
import { appConfig } from '../../config/app';

const ImageUpload = ({ 
  images = [], 
  onImagesChange, 
  uploadPath = 'products',
  maxImages = 10,
  label = 'Upload Images',
  helperText = 'Upload high-quality images for your product',
  multiple = true,
  showPreviews = true
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const fileInputRef = useRef(null);

  // Handle file selection
  const handleFileSelect = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    // Check max images limit
    if (images.length + files.length > maxImages) {
      setError(`Maximum ${maxImages} images allowed`);
      return;
    }

    setUploading(true);
    setError('');

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('path', uploadPath);

        const response = await axios.post(`${appConfig.api.baseURL}/images/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data.success) {
          return response.data.data.url;
        } else {
          throw new Error(response.data.message || 'Upload failed');
        }
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      const newImages = multiple ? [...images, ...uploadedUrls] : uploadedUrls;
      onImagesChange(newImages);
    } catch (error) {
      console.error('Error uploading images:', error);
      setError(error.response?.data?.message || 'Failed to upload images');
    } finally {
      setUploading(false);
    }
  }, [images, onImagesChange, uploadPath, maxImages, multiple]);

  // Handle file input change
  const handleInputChange = (event) => {
    const files = event.target.files;
    handleFileSelect(files);
    // Clear input value to allow re-uploading the same file
    event.target.value = '';
  };

  // Handle drag and drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  // Remove image
  const handleRemoveImage = (indexToRemove) => {
    const newImages = images.filter((_, index) => index !== indexToRemove);
    onImagesChange(newImages);
  };

  // Get filename from URL
  const getImageFilename = (url) => {
    return url.split('/').pop() || 'image';
  };

  // Handle image click to open modal
  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setImageModalOpen(true);
  };

  // Close image modal
  const closeImageModal = () => {
    setSelectedImage(null);
    setImageModalOpen(false);
  };

  return (
    <Box>
      {/* Image Thumbnails */}
      {showPreviews && images.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ImageIcon fontSize="small" />
            Uploaded Images ({images.length}/{maxImages})
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {images.map((imageUrl, index) => (
              <Box
                key={index}
                sx={{
                  position: 'relative',
                  width: 180,
                  height: 180,
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'grey.100',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8
                  }
                }}
                onClick={() => handleImageClick(imageUrl)}
              >
                <Box
                  component="img"
                  src={imageUrl}
                  alt={`Upload ${index + 1}`}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                />
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    width: 32,
                    height: 32,
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '2px solid rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                      border: '2px solid rgba(255, 255, 255, 1)'
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage(index);
                  }}
                  size="small"
                >
                  <Delete 
                    sx={{ 
                      fontSize: 18,
                      border: '1px solid #d32f2f',
                      borderRadius: '50%',
                      padding: '2px'
                    }} 
                    color="error" 
                  />
                </IconButton>
                {index === 0 && (
                  <Chip
                    label="Main"
                    size="small"
                    color="primary"
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      left: 8,
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      '& .MuiChip-label': {
                        px: 1
                      }
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Upload Button */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <input
          type="file"
          multiple={multiple}
          accept="image/*"
          onChange={handleInputChange}
          style={{ display: 'none' }}
          disabled={uploading}
          ref={fileInputRef}
        />

        <Button
          variant="outlined"
          startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
          onClick={() => {
            if (!uploading && fileInputRef.current) {
              fileInputRef.current.click();
            }
          }}
          disabled={uploading}
          sx={{ borderRadius: 2 }}
        >
          {uploading ? 'Uploading...' : label}
        </Button>
        
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Max {maxImages} images • Up to 5MB each • JPEG, PNG, GIF, WebP
        </Typography>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Image Preview Modal */}
      <Dialog
        open={imageModalOpen}
        onClose={closeImageModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            boxShadow: 'none'
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={closeImageModal}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)'
              },
              zIndex: 1
            }}
          >
            <Close />
          </IconButton>
          {selectedImage && (
            <Box
              component="img"
              src={selectedImage}
              alt="Preview"
              sx={{
                width: '100%',
                height: 'auto',
                display: 'block',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ImageUpload; 